import React, { useEffect, useRef } from "react";
import { X } from "lucide-react";

/**
 * BottomSheet — Modal yang muncul dari bawah layar (mobile-native feel).
 * Hanya tampil di mobile (di desktop bisa dipakai sebagai dialog biasa).
 *
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - title?: string
 * - children: ReactNode
 * - className?: string (untuk mengatur max-height atau style tambahan)
 */
export default function BottomSheet({
  isOpen,
  onClose,
  title,
  children,
  className = "",
}) {
  const sheetRef = useRef(null);

  // Lock body scroll saat sheet terbuka
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Tutup saat tekan Escape
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape" && isOpen) onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center"
      role="dialog"
      aria-modal="true"
      aria-label={title || "Menu"}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        style={{ animation: "fade-in 0.2s ease-out" }}
        onClick={onClose}
      />

      {/* Sheet */}
      <div
        ref={sheetRef}
        className={`
          relative w-full bg-white dark:bg-slate-900
          rounded-t-[2rem] shadow-2xl
          max-h-[85vh] flex flex-col
          ${className}
        `}
        style={{ animation: "sheet-slide-up 0.3s cubic-bezier(0.16, 1, 0.3, 1)" }}
      >
        {/* Drag Handle */}
        <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-slate-600" />
        </div>

        {/* Header */}
        {title && (
          <div className="flex items-center justify-between px-6 py-3 border-b border-gray-100 dark:border-slate-800 flex-shrink-0">
            <h2 className="text-base font-bold text-gray-900 dark:text-white">
              {title}
            </h2>
            <button
              onClick={onClose}
              aria-label="Tutup"
              className="p-2 rounded-xl bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors active:scale-90"
            >
              <X size={18} className="text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        )}

        {/* Content */}
        <div className="overflow-y-auto touch-scroll flex-1 pb-safe">
          {children}
        </div>
      </div>

      <style>{`
        @keyframes sheet-slide-up {
          from { transform: translateY(100%); opacity: 0.8; }
          to   { transform: translateY(0);    opacity: 1; }
        }
      `}</style>
    </div>
  );
}
